import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { QrCode, CheckCircle, WarningOctagon, XCircle, MapPinLine, Shield, Crosshair } from "@phosphor-icons/react";

const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy_m: pos.coords.accuracy }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
  );
});

const Scan = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/scan/${token}`);
      setInfo(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid checkpoint QR");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { const id = setTimeout(load, 0); return () => clearTimeout(id); }, [load]);

  const recordScan = useCallback(async () => {
    setBusy(true);
    const pos = await getPosition();
    try {
      const { data } = await api.post(`/scan/${token}`, pos || {});
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to record scan");
    } finally { setBusy(false); }
  }, [token]);

  // Auto-submit on page load (guard scans QR -> instant log)
  useEffect(() => {
    if (info && !result && !busy) {
      const id = setTimeout(recordScan, 300);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [info, result, busy, recordScan]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500 label-mono">LOADING...</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="tac-card p-10 max-w-md text-center">
          <XCircle size={36} className="mx-auto text-red-500 mb-3" weight="duotone"/>
          <h1 className="font-display text-2xl font-bold">{error}</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-grid scanline" data-testid="scan-page">
      <div className="max-w-md mx-auto p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 grid place-items-center bg-[#007AFF]"><Shield size={20} weight="fill"/></div>
          <div>
            <div className="font-display font-black text-xl">SANDY</div>
            <div className="label-mono">Checkpoint Scan</div>
          </div>
        </div>

        <div className="tac-card p-6 mb-6">
          <div className="label-mono mb-2">// CHECKPOINT</div>
          <h1 className="font-display text-2xl font-bold mb-1">{info.checkpoint_name}</h1>
          <div className="text-sm text-zinc-400 flex items-center gap-2 mt-2">
            <MapPinLine size={14} className="text-[#2BD4D9]"/>
            {info.site_name}
          </div>
          <div className="text-xs text-zinc-500 mt-1">{info.site_address}</div>
        </div>

        {!result ? (
          <div className="tac-card p-8 text-center">
            <QrCode size={40} className="mx-auto text-[#2BD4D9] mb-3" weight="duotone"/>
            <h2 className="font-display text-lg font-bold mb-1">{busy ? "Logging scan..." : "Recording your scan"}</h2>
            <p className="text-zinc-500 text-xs mt-2">
              {busy ? "Capturing your location for verification." : "Tap allow if your browser asks for location permission."}
            </p>
            {!busy && (
              <button data-testid="scan-retry-btn" onClick={recordScan} className="mt-4 px-4 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold">
                <Crosshair size={12} className="inline -mt-0.5 mr-1"/>Record now
              </button>
            )}
          </div>
        ) : (
          <div className="tac-card p-8 text-center" data-testid="scan-result">
            {result.verified === false ? (
              <>
                <WarningOctagon size={36} className="mx-auto text-yellow-400 mb-3" weight="duotone"/>
                <h2 className="font-display text-xl font-bold mb-1">Logged — but outside fence</h2>
                <p className="text-zinc-400 text-sm">{Math.round(result.distance_m)}m from {result.site_name}</p>
                <p className="text-zinc-500 text-xs mt-3">Dispatcher has been notified.</p>
              </>
            ) : result.verified === true ? (
              <>
                <CheckCircle size={36} className="mx-auto text-emerald-500 mb-3" weight="duotone"/>
                <h2 className="font-display text-xl font-bold mb-1">Checkpoint logged ✓</h2>
                <p className="text-zinc-400 text-sm">On-site at {Math.round(result.distance_m)}m</p>
                {result.shift_linked && <p className="text-emerald-400 text-xs mt-3 font-mono uppercase">Linked to active shift</p>}
              </>
            ) : (
              <>
                <CheckCircle size={36} className="mx-auto text-emerald-500 mb-3" weight="duotone"/>
                <h2 className="font-display text-xl font-bold mb-1">Checkpoint logged</h2>
                <p className="text-zinc-500 text-xs mt-3">Location not captured. Allow location next time for GPS verification.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Scan;
